import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { adminApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { FinancialModelForm } from "@/components/FinancialModelForm";
import { OwnerLetter } from "@/components/OwnerLetter";
import {
  Loader2,
  Sparkles,
  UploadCloud,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ExternalLink,
} from "lucide-react";

const initialForm = {
  market: "",
  property_type: "",
  address: "",
  asking_price_usd: "",
  monthly_rent_usd: "",
  bedrooms: "",
  bathrooms: "",
  square_feet: "",
  year_built: "",
  notes: "",
  contact_email: "",
  contact_first_name: "",
  contact_last_name: "",
  contact_phone: "",
};

function toPayload(form) {
  const p = { ...form };
  ["asking_price_usd", "monthly_rent_usd", "bedrooms", "bathrooms", "square_feet", "year_built"].forEach(
    (k) => {
      if (p[k] === "" || p[k] === null || p[k] === undefined) delete p[k];
      else p[k] = Number(p[k]);
    }
  );
  Object.keys(p).forEach((k) => {
    if (p[k] === "" || p[k] === null || p[k] === undefined) delete p[k];
  });
  return p;
}

export default function AdminScout() {
  const [form, setForm] = useState(initialForm);
  const [analyzing, setAnalyzing] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [scoutHistory, setScoutHistory] = useState([]);
  const [hsStatus, setHsStatus] = useState(null);

  const loadHistory = async () => {
    try {
      const [scoutRes, hsRes] = await Promise.all([
        adminApi.get("/admin/property-scout"),
        adminApi.get("/admin/integrations/hubspot"),
      ]);
      setScoutHistory(scoutRes.data || []);
      setHsStatus(hsRes.data);
    } catch (_e) {
      /* handled by interceptor */
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const analyze = async () => {
    if (!form.market || form.market.trim().length < 2) {
      toast.error("Please enter a market (city, ZIP, or neighborhood).");
      return;
    }
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const payload = toPayload(form);
      // Strip contact fields — analyze endpoint doesn't need them
      [
        "contact_email",
        "contact_first_name",
        "contact_last_name",
        "contact_phone",
      ].forEach((k) => delete payload[k]);
      const r = await adminApi.post("/admin/property-scout/analyze", payload);
      setAnalysis(r.data);
      toast.success("Sasha's analysis is ready.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed.");
    } finally {
      setAnalyzing(false);
    }
  };

  const pushToHubSpot = async () => {
    if (!form.contact_email) {
      toast.error("Contact email is required to push to HubSpot.");
      return;
    }
    if (!hsStatus?.configured) {
      toast.error("Connect HubSpot in Settings first.");
      return;
    }
    setPushing(true);
    try {
      const payload = toPayload(form);
      if (analysis) payload.analysis = analysis;
      const r = await adminApi.post("/admin/property-scout/push", payload);
      toast.success("Pushed to HubSpot — contact, deal & note created.");
      setForm(initialForm);
      setAnalysis(null);
      setScoutHistory((h) => [r.data, ...h]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not push to HubSpot.");
    } finally {
      setPushing(false);
    }
  };

  return (
    <main
      data-testid="admin-scout"
      className="max-w-6xl mx-auto px-6 md:px-10 py-10 lg:py-14"
    >
      <div className="eyebrow">Studio</div>
      <h1 className="font-serif-display text-4xl lg:text-5xl mt-3 text-[#2A1F1A]">
        Property Scout
      </h1>
      <p className="mt-3 text-sm text-[#6E6E70] max-w-2xl">
        Ask Sasha to weigh a property or a market across three angles —{" "}
        <em>lease</em>, <em>owner financing</em> and <em>property management</em>.
        Push the resulting Go/No-Go analysis into HubSpot so your Breeze
        workflows can pick it up and route the communications.
      </p>

      {!hsStatus?.configured && (
        <div
          data-testid="scout-hubspot-warning"
          className="mt-6 border border-[#C03028] bg-[#FDECEA] px-4 py-3 text-sm text-[#2A1F1A] flex items-start gap-3"
        >
          <AlertTriangle size={16} className="text-[#C03028] mt-0.5" />
          <div>
            HubSpot isn&apos;t connected yet — you can still run the analysis, but
            it won&apos;t reach HubSpot until you paste your Private App token in{" "}
            <a
              href="/admin/settings"
              className="text-[#C03028] underline inline-flex items-center gap-1"
            >
              Settings <ExternalLink size={11} />
            </a>
            .
          </div>
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left: form */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          data-testid="scout-form-card"
          className="bg-white border border-[#E3DED5] p-6 lg:p-8"
        >
          <div className="eyebrow">Property brief</div>
          <h2 className="font-serif-display text-2xl text-[#2A1F1A] mt-1">
            Tell Sasha about the property
          </h2>

          <div className="mt-6 space-y-4">
            <Field
              label="Market (city, ZIP, or neighborhood)"
              required
              testid="scout-market"
              value={form.market}
              onChange={update("market")}
              placeholder="Atlanta, GA · 30303"
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Property type"
                testid="scout-property-type"
                value={form.property_type}
                onChange={update("property_type")}
                placeholder="Single-family"
              />
              <Field
                label="Address"
                testid="scout-address"
                value={form.address}
                onChange={update("address")}
                placeholder="123 Peachtree St"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Asking price (USD)"
                testid="scout-asking"
                value={form.asking_price_usd}
                onChange={update("asking_price_usd")}
                placeholder="450000"
                type="number"
              />
              <Field
                label="Monthly rent (USD)"
                testid="scout-rent"
                value={form.monthly_rent_usd}
                onChange={update("monthly_rent_usd")}
                placeholder="3200"
                type="number"
              />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <Field
                label="Beds"
                testid="scout-bedrooms"
                value={form.bedrooms}
                onChange={update("bedrooms")}
                type="number"
              />
              <Field
                label="Baths"
                testid="scout-bathrooms"
                value={form.bathrooms}
                onChange={update("bathrooms")}
                type="number"
              />
              <Field
                label="Sq ft"
                testid="scout-sqft"
                value={form.square_feet}
                onChange={update("square_feet")}
                type="number"
              />
              <Field
                label="Year built"
                testid="scout-year"
                value={form.year_built}
                onChange={update("year_built")}
                type="number"
              />
            </div>
            <div>
              <Label className="eyebrow">Notes for Sasha</Label>
              <Textarea
                data-testid="scout-notes"
                value={form.notes}
                onChange={update("notes")}
                placeholder="Anything else worth knowing — HOA rules, deferred maintenance, seller motivation…"
                className="editorial-input mt-2 min-h-[90px]"
              />
            </div>
          </div>

          <div className="mt-6 border-t border-[#E3DED5] pt-6">
            <div className="eyebrow mb-3">HubSpot contact</div>
            <Field
              label="Contact email"
              required
              testid="scout-contact-email"
              value={form.contact_email}
              onChange={update("contact_email")}
              placeholder="owner@example.com"
              type="email"
            />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field
                label="First name"
                testid="scout-contact-first"
                value={form.contact_first_name}
                onChange={update("contact_first_name")}
              />
              <Field
                label="Last name"
                testid="scout-contact-last"
                value={form.contact_last_name}
                onChange={update("contact_last_name")}
              />
            </div>
            <Field
              label="Phone"
              testid="scout-contact-phone"
              value={form.contact_phone}
              onChange={update("contact_phone")}
              placeholder="+1 555 555 5555"
              className="mt-3"
            />
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={analyze}
              disabled={analyzing}
              data-testid="scout-analyze-btn"
              className="btn-secondary inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm flex-1"
            >
              {analyzing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Sparkles size={14} />
              )}
              {analyzing ? "Sasha is thinking…" : "Run Sasha's analysis"}
            </button>
            <button
              type="button"
              onClick={pushToHubSpot}
              disabled={pushing || !hsStatus?.configured}
              data-testid="scout-push-btn"
              className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm flex-1"
              title={
                hsStatus?.configured
                  ? "Push to HubSpot"
                  : "Connect HubSpot in Settings first"
              }
            >
              {pushing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <UploadCloud size={14} />
              )}
              {pushing ? "Pushing…" : "Push to HubSpot"}
            </button>
          </div>
        </motion.section>

        {/* Right: analysis */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
          data-testid="scout-analysis-card"
          className="bg-white border border-[#E3DED5] p-6 lg:p-8"
        >
          <div className="eyebrow">Analysis</div>
          <h2 className="font-serif-display text-2xl text-[#2A1F1A] mt-1">
            Go / No-Go
          </h2>

          {!analysis && !analyzing && (
            <div
              data-testid="scout-analysis-empty"
              className="mt-6 text-sm text-[#6E6E70]"
            >
              Fill in the brief on the left and run the analysis. Sasha will
              return a Go/No-Go call for lease, owner financing, and property
              management.
            </div>
          )}

          {analyzing && (
            <div className="mt-6 text-sm text-[#6E6E70] inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              Sasha is reviewing the market and running the numbers…
            </div>
          )}

          {analysis && (
            <div className="mt-6 space-y-5" data-testid="scout-analysis-result">
              <div className="border border-[#E3DED5] p-4">
                <div className="flex items-center justify-between">
                  <div className="eyebrow">Overall</div>
                  <VerdictPill verdict={analysis.overall} />
                </div>
                {analysis.market_summary && (
                  <p className="mt-2 text-sm text-[#2A1F1A]">
                    {analysis.market_summary}
                  </p>
                )}
              </div>

              {(analysis.opportunities || []).map((opp) => (
                <OpportunityCard key={opp.type} opp={opp} />
              ))}

              <OwnerLetter
                analysis={analysis}
                buildPayload={() => {
                  const p = toPayload(form);
                  ["contact_email", "contact_first_name", "contact_last_name", "contact_phone"].forEach(
                    (k) => delete p[k]
                  );
                  return p;
                }}
              />

              {analysis.next_steps && analysis.next_steps.length > 0 && (
                <div className="border border-[#E3DED5] p-4">
                  <div className="eyebrow mb-2">Next steps</div>
                  <ul className="text-sm text-[#2A1F1A] space-y-1">
                    {analysis.next_steps.map((s, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-[#B0895C] mt-0.5">·</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </motion.section>
      </div>

      {/* Thrive Financial Model (deck p.8) */}
      <FinancialModelForm
        hsConfigured={hsStatus?.configured}
        defaultEmail={form.contact_email}
        scoutBrief={toPayload(form)}
      />

      {/* History */}
      <section
        data-testid="scout-history"
        className="mt-14 bg-white border border-[#E3DED5]"
      >
        <div className="px-6 py-4 border-b border-[#E3DED5] flex items-center justify-between">
          <div>
            <div className="eyebrow">Recently pushed</div>
            <h3 className="font-serif-display text-xl text-[#2A1F1A]">
              Scouted properties
            </h3>
          </div>
        </div>
        {scoutHistory.length === 0 ? (
          <div className="px-6 py-10 text-sm text-[#6E6E70] text-center">
            No properties pushed to HubSpot yet.
          </div>
        ) : (
          scoutHistory.map((s) => (
            <div
              key={s.id}
              data-testid={`scout-row-${s.id}`}
              className="px-6 py-4 border-b border-[#E3DED5] last:border-b-0 grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
            >
              <div className="md:col-span-4">
                <div className="text-[#2A1F1A] font-medium">{s.market}</div>
                <div className="text-xs text-[#6E6E70]">
                  {s.address || s.property_type || "—"}
                </div>
              </div>
              <div className="md:col-span-3 text-sm text-[#2A1F1A]">
                {s.asking_price_usd
                  ? `$${Number(s.asking_price_usd).toLocaleString()}`
                  : "—"}
              </div>
              <div className="md:col-span-2">
                {s.analysis?.overall ? (
                  <VerdictPill verdict={s.analysis.overall} />
                ) : (
                  <span className="text-xs text-[#6E6E70]">No analysis</span>
                )}
              </div>
              <div className="md:col-span-3 text-xs text-[#6E6E70] md:text-right">
                Contact {s.hubspot_contact_id || "—"} · Deal{" "}
                {s.hubspot_deal_id || "—"}
              </div>
            </div>
          ))
        )}
      </section>
    </main>
  );
}

function Field({
  label,
  required,
  testid,
  value,
  onChange,
  placeholder,
  type = "text",
  className = "",
}) {
  return (
    <div className={className}>
      <Label className="eyebrow">
        {label}
        {required && <span className="text-[#C03028] ml-1">·</span>}
      </Label>
      <Input
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type={type}
        className="editorial-input mt-2"
      />
    </div>
  );
}

function VerdictPill({ verdict }) {
  const v = (verdict || "REVIEW").toUpperCase();
  const styles =
    v === "GO"
      ? { bg: "bg-[#2D4C3E]", text: "text-[#FAF9F6]", Icon: CheckCircle2 }
      : v === "NO_GO"
        ? { bg: "bg-[#C03028]", text: "text-[#FAF9F6]", Icon: XCircle }
        : { bg: "bg-[#B0895C]", text: "text-[#FAF9F6]", Icon: AlertTriangle };
  const { bg, text, Icon } = styles;
  return (
    <span
      data-testid={`verdict-pill-${v}`}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold",
        bg,
        text
      )}
    >
      <Icon size={12} /> {v.replace("_", " ")}
    </span>
  );
}

function OpportunityCard({ opp }) {
  const kind = String(opp.type || "").replace(/_/g, " ");
  const score = opp.score ?? null;
  return (
    <div
      data-testid={`opportunity-${opp.type}`}
      className="border border-[#E3DED5] p-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow">{kind}</div>
          {score !== null && (
            <div className="font-serif-display text-2xl text-[#2A1F1A] mt-0.5">
              {score}
              <span className="text-sm text-[#8E8F90]">/100</span>
            </div>
          )}
        </div>
        <VerdictPill verdict={opp.recommendation} />
      </div>
      {(opp.reasons || []).length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#2D4C3E] font-semibold mb-1">
            Why go
          </div>
          <ul className="text-sm text-[#2A1F1A] space-y-1">
            {opp.reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2
                  size={12}
                  className="text-[#2D4C3E] mt-1 flex-shrink-0"
                />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(opp.concerns || []).length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#C03028] font-semibold mb-1">
            Concerns
          </div>
          <ul className="text-sm text-[#2A1F1A] space-y-1">
            {opp.concerns.map((c, i) => (
              <li key={i} className="flex items-start gap-2">
                <XCircle
                  size={12}
                  className="text-[#C03028] mt-1 flex-shrink-0"
                />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
